
import { Component, Prop, Mixins } from 'vue-property-decorator';
import { HorizontalBar } from 'vue-chartjs';
import { ChartConfig } from './chartConfig';
import {Data, DataFilter, Series, SeriesConfig, SeriesConfigType} from '../../models';
import Chart, {ChartData, ChartTooltipItem, ChartTooltipModel} from 'chart.js';
import { chartDataUtil } from '@/features/reports/util/ChartDataUtil';
import ColourService from '@/shared/services/color';
import WordWrapUtil from '@/shared/util/wordWrapUtil';

@Component
export default class HorizontalThresholdBarChart extends Mixins(HorizontalBar) {
	@Prop() public data: Data;
	@Prop() public dataFilter: DataFilter;
	@Prop() public barColour: string;

	@Prop() public thresholdData: number;

	@Prop() public thresholdLineColour: string;
	@Prop() public orderAsc: boolean;

	public mounted() {
		const data = this.mapData();
		const options = this.overrideOptions(ChartConfig.gridChartsGlobalOptions);
		this.renderChart(data, options);
	}

	public overrideOptions(options: Chart.ChartOptions): Chart.ChartOptions {
		options.plugins!.inlineLabels = false;
		options.plugins!.crosshair = false;
		options.legend!.display = false;
		options.scales = {
			xAxes: [{
				scaleLabel: {
					labelString: this.data.scale.title
				},
				ticks: {
					beginAtZero: true,
					max: 1,
					maxTicksLimit: 20
				}
			}],
			yAxes: [{
				ticks: {
					callback: (value: string) => {
						const truncated = value.substr(0, 15);
						return truncated.length === value.length
							? truncated
							: `${truncated.trim()}...`;
					}
				},
				gridLines: {
					display: false
				},
				afterFit: (instance) => {
					// set the width of labels to the longest label
					// with an amount of padding as a buffer
					instance.width = instance.longestLabelWidth + 10;
				}
			}]
		};
		options.tooltips!.custom = (tooltip: ChartTooltipModel) => {
			tooltip.displayColors = false;
		};
		options.tooltips!.callbacks = {
			title: (tooltipItems: Chart.ChartTooltipItem[]) => {
				const tooltipItem = tooltipItems[0];
				const lineLength = 24;
				// only break characters if there are single words
				// longer than the limit to keep words in-tact as much as possible
				let name = tooltipItem.label as string;
				name = WordWrapUtil.breakWordsOverLimit(name, lineLength);
				// return name;
				return WordWrapUtil.wrap(name, lineLength);
			},
			label: (tooltipItem: Chart.ChartTooltipItem) => {
				const score = parseFloat(tooltipItem.value as string).toFixed(3);
				return `Average Score: ${score}`;
			}
		};
		options.annotation = {
			annotations: [
				{
					type: 'line',
					mode: 'vertical',
					scaleID: 'x-axis-0',
					value: this.lineMarkerPosition,
					borderColor: this.thresholdLineColour,
					borderDash: [8],
					borderWidth: 2
				}
			]
		};
		return options;
	}

	public mapData(): ChartData {
		const series = this.data.series[0];
		const orderedData = chartDataUtil.orderData({ data: series.data, labels: this.data.scale.labels }, {
			dataOrderAscending: this.orderAsc,
			forceZeroValuesToBottom: true
		});

		return {
			labels: orderedData.labels,
			datasets: [
				{
					label: series.displayName,
					data: orderedData.data,
					backgroundColor: this.barColour,
					borderColor: this.barColour,
					hoverBackgroundColor: ColourService.setAlpha(this.barColour, 0.4),
					maxBarThickness: 32
				}
			]
		};
	}

	private get lineMarkerPosition(): number {
		return this.thresholdData;
	}
}
